import * as LaunchDarkly from 'launchdarkly-js-client-sdk';

import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { shareReplay, take, tap } from 'rxjs/operators';

import { SessiebeheerResource, UserType } from '@ap/interface-apic-sessiebeheer';
import { ContractDetails, MijnOverzichtResource } from '@ap/interface-apix-mijn-overzicht';
import { MijnJwtService, MijnSession } from '@ap/mijn-auth-sessie-manager';
import { ApThemeService } from '@ap/theme';

import { LAUNCH_DARKLY_CLIENT_SIDE_ID, LAUNCH_DARKLY_DEV_CLIENT_SIDE_ID } from './mijn-launchdarkly.model';

@Injectable({ providedIn: 'root' })
export class MijnLaunchDarklyService {
    private readonly themeService: ApThemeService = inject(ApThemeService);
    private readonly mijnOverzichtResource: MijnOverzichtResource = inject(MijnOverzichtResource);
    private readonly sessiebeheerResource: SessiebeheerResource = inject(SessiebeheerResource);
    private readonly mijnJwtService: MijnJwtService = inject(MijnJwtService);
    private readonly client: LaunchDarkly.LDClient;
    private context: LaunchDarkly.LDSingleKindContext;
    private sessionInFlight$: Observable<MijnSession | undefined> | null = null;

    public constructor() {
        const clientSideId = LAUNCH_DARKLY_CLIENT_SIDE_ID.includes('LAUNCH_DARKLY_CLIENT_SIDE_ID')
            ? LAUNCH_DARKLY_DEV_CLIENT_SIDE_ID
            : LAUNCH_DARKLY_CLIENT_SIDE_ID;

        this.context = {
            kind: 'session',
            platform: 'web',
            label: this.themeService.getTheme().toUpperCase(),
            anonymous: true
        };

        this.client = LaunchDarkly.initialize(clientSideId, this.context, {
            streamUrl: 'https://clientstream.eu.launchdarkly.com',
            baseUrl: 'https://clientsdk.eu.launchdarkly.com',
            eventsUrl: 'https://events.eu.launchdarkly.com',
            logger: LaunchDarkly.basicLogger({
                level: 'none'
            }),
            bootstrap: 'localStorage',
            sendEvents: true,
            streaming: true
        });

        this.client.on('error', () => {
            // Capture the error so it's not visibly thrown.
            // See: https://support.launchdarkly.com/hc/en-us/articles/12998125691419-Error-LaunchDarklyFlagFetchError-network-error
        });
    }

    /**
     * @description
     *  Updates the launchdarkly context when needed.
     * @returns a Promise which resolves once the flag values for the new context are available,
     * providing an LDFlagSet containing the new values.
     */
    public updateContext(): Observable<{ session: MijnSession | null; contractDetails: ContractDetails | null }> {
        return this.getSession().pipe(
            switchMap((session) => {
                if (session?.userId && this.context.key == null) {
                    return this.mijnOverzichtResource.getContractDetails().pipe(map((contractDetails) => ({ session, contractDetails })));
                }
                return of({ session: null, contractDetails: null });
            }),
            tap(({ session, contractDetails }) => {
                if (session) {
                    this.context = {
                        kind: 'session',
                        platform: 'web',
                        label: this.themeService.getTheme().toUpperCase(),
                        anonymous: false,
                        key: session.userId,
                        session: session.sessionId,
                        customerType: contractDetails.is_zakelijk ? 'business' : 'private'
                    };
                    this.client.waitForInitialization(5).then(() => {
                        return this.client.identify(this.context);
                    });
                }
            })
        );
    }

    private getSession(): Observable<MijnSession | undefined> {
        if (this.sessionInFlight$) {
            return this.sessionInFlight$;
        }

        const session$ = this.mijnJwtService.isAuthenticated$.pipe(
            take(1),
            switchMap((authenticated) => {
                if (authenticated) {
                    return this.mijnJwtService.customerId$.pipe(
                        take(1),
                        switchMap((userId) =>
                            this.mijnJwtService.contextId$.pipe(
                                take(1),
                                map((sessionId) => ({ userId: userId ?? undefined, sessionId: sessionId ?? undefined }))
                            )
                        )
                    );
                }

                return this.sessiebeheerResource
                    .getSession('0')
                    .pipe(
                        map((session) =>
                            session?.user && !session.user.type.includes(UserType.ANONYMOUS)
                                ? { sessionId: session.id, userId: session.user.id, userType: session.user.type }
                                : undefined
                        )
                    );
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        session$.subscribe({
            error: () => (this.sessionInFlight$ = null),
            complete: () => (this.sessionInFlight$ = null)
        });

        this.sessionInFlight$ = session$;

        return this.sessionInFlight$;
    }
    /**
     * @description
     *  Gets a flag value, setting a default value. Also allows you to subscribe to changes.
     * @param key
     *  The unique key of the feature flag.
     * @param defaultValue
     *  The default value of the flag, to be used if the value is not available from LaunchDarkly.
     * @param subscribe
     *  Whether to subscribe to changes or not.
     * @returns
     *  an Observable of the flag value.
     */
    public getFlagValue<T>(key: string, defaultValue?: T, subscribe = false): Observable<T> {
        return new Observable<T>((observer) => {
            const initializeClient = async () => {
                try {
                    await this.client.waitForInitialization(5);
                    this.updateContext().subscribe(() => {
                        const initialFlagValue = this.client.variation(key, defaultValue);
                        observer.next(initialFlagValue);

                        if (subscribe) {
                            this.client.on(`change:${key}`, (newValue) => {
                                observer.next(newValue);
                            });
                        } else {
                            observer.complete();
                        }
                    });
                } catch (error) {
                    observer.error(error);
                }
            };

            initializeClient();
        });
    }
}
